import React from 'react';

import type {
	ExtensionHandler,
	ExtensionParams,
} from '@atlaskit/editor-common/extensions/extension-handler';

import { REDACTION_EXTENSION_TYPE, type RedactionExtensionParams } from '../redaction-constants.ts';

import {
	RedactionLoading,
	TourableRedactionExtensionLoader,
} from './TourableRedactionExtensionLoader.tsx';

// Cheap pre-check on the serialized ADF, in the same style as the Team Calendar and
// legacy-content checks in the content renderer, so pages without a redaction keep
// their handler sets untouched.
const REDACTION_EXTENSION_TYPE_REGEXP = new RegExp(
	`"extensionType"\\s*:\\s*"${REDACTION_EXTENSION_TYPE.replace(/\./g, '\\.')}"`,
);

/** Whether a serialized ADF document contains at least one Guard redaction node. */
export const hasRedactionExtensionNode = (adf?: string | null): boolean =>
	Boolean(adf) && REDACTION_EXTENSION_TYPE_REGEXP.test(adf as string);

/**
 * Renderer `extensionHandlers` entry for the server-rendered pass and the matching
 * first client render (hydration).
 *
 * Left to the extension manifest, a redaction node renders through the async
 * `render()` pipeline: until it resolves, the renderer paints an empty `<div>`
 * inside the paragraph. In server HTML that `<div>` closes the surrounding `<p>`
 * (the stray line break on first paint) and, once hydrated, it collapses to
 * nothing (the missing space). A handler is consulted synchronously and rendered
 * without that wrapper, so the redaction reserves its inline footprint in the very
 * first HTML.
 *
 * The full extension is deliberately not rendered here: `LoadablePaint` mounts the
 * real module on the server, and the onboarding-tour hooks would issue license and
 * permission queries during SSR. Only the sized placeholder (`RedactionLoading`)
 * is emitted; the client hands over to {@link getRedactionExtensionHandler} once
 * the extension manifest is ready.
 */
export const getRedactionSsrExtensionHandler =
	(): ExtensionHandler<RedactionExtensionParams> => () => <RedactionLoading />;

/**
 * Renderer `extensionHandlers` entry for the client once the extension manifest is
 * ready. Keeping the redaction on the synchronous handler path, rather than
 * releasing it to the manifest's async render, means the space the SSR placeholder
 * reserved is never blanked; the lazy extension mounts in place, with that same
 * placeholder as its own loading state until the scribble draws.
 */
export const getRedactionExtensionHandler =
	(contentId?: string | null): ExtensionHandler<RedactionExtensionParams> =>
	(extension: ExtensionParams<RedactionExtensionParams>) => (
		<TourableRedactionExtensionLoader contentId={contentId} extensionNode={extension} />
	);
